import { useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import sortBy from 'lodash/sortBy';

import {
  SettingsGroup,
  SettingsLabel,
} from 'approot/activity-builder/settings/settings.style';
import {
  InteractiveResponseItem,
  MIN_LABEL_WIDTH,
  SettingProps,
  RESPONSE_FREE,
} from './interactive-response.constants';
import { PStyled } from 'approot/shared/styles/typography.style';
import { HrStyled } from 'approot/shared/styles/decorative.style';
import {
  Sortable,
  SortableContent,
  SortableHeader,
  SortableItem,
} from 'approot/shared/sortable/sortable';
import { getReorderSort } from 'approot/shared/sortable/sortable.utils';
import { ImageThumbnailStatic } from 'approot/shared/images/images';
import { RangeFieldSetting } from 'approot/activity-builder/components/shared/range-field';
import Switch from 'approot/shared/switch/switch';
import AutoResizingTextArea from 'approot/shared/auto-resizing-textarea/auto-resizing-textarea';
import { useInteractiveResponseUpdate } from './interactive-response-settings.hooks';
import * as Styled from './interactive-response-settings.style';
import { FlexSpaceBetween } from 'approot/shared/styles/flex.style';
import { InteractiveResponseFeedbackSetting } from '../../shared/interactive-response-shared';
import { CheckableSingle } from 'approot/shared/styles/buttons/checkable-input';
import { SmallStyled } from 'approot/shared/styles/typography.style';
import { isTextVariant } from '../../shared/interactive-response-shared.utils';
import {
  InteractiveDisplayItem,
  InteractiveDragContainer,
} from 'approot/activity-builder/components/shared/interactive-drag-drop/interactive-drag.style';
import InteractiveResponseRichtextView from '../../shared/interactive-response-richtext-view';
import { useGetInteractiveLessonParams } from 'approot/activity-builder/activity-builder.hooks';
import {
  ACTIVITIES_ANSWER_MODE_FIXED,
  LESSON_ACTIVITIES,
} from 'global/constants';

const LABEL_LENGTH = 'labelLength';

type FormData = {
  [LABEL_LENGTH]?: number;
};

const InteractiveResponseSolution = (props: SettingProps) => {
  const { properties } = props;

  const { contentType } = useGetInteractiveLessonParams();

  const items = useMemo(() => {
    // Use parent variant if not item variant is set
    return sortBy(properties.interactiveResponse.items, 'sort').map(item => ({
      ...item,
      variant: item.variant || properties.interactiveResponse.variant,
    }));
  }, [
    properties.interactiveResponse.items,
    properties.interactiveResponse.variant,
  ]);

  const formMethods = useForm<FormData>({
    mode: 'onChange',
    defaultValues: {
      [LABEL_LENGTH]:
        properties.interactiveResponse.labelLength || MIN_LABEL_WIDTH,
    },
  });
  const labelLength = formMethods.watch(LABEL_LENGTH);

  const { handleUpdate } = useInteractiveResponseUpdate(props);

  return (
    <FormProvider {...formMethods}>
      <SettingsGroup>
        <RangeFieldSetting
          fieldName={LABEL_LENGTH}
          label="Label Length"
          max={224}
          min={MIN_LABEL_WIDTH}
          postfix="px"
          value={labelLength || MIN_LABEL_WIDTH}
          onChange={value =>
            handleUpdate({
              ...properties,
              interactiveResponse: {
                ...properties.interactiveResponse,
                labelLength: value,
              },
            })
          }
        />
      </SettingsGroup>

      <SettingsGroup>
        <SettingsLabel>Drop Zone Border</SettingsLabel>
        <Switch
          ariaLabel={`${
            properties.interactiveResponse.labelBorder ? 'Hide' : 'Show'
          } Drop Zone Border`}
          state={!!properties.interactiveResponse.labelBorder}
          onClick={() =>
            handleUpdate({
              ...properties,
              interactiveResponse: {
                ...properties.interactiveResponse,
                labelBorder: !properties.interactiveResponse.labelBorder,
              },
            })
          }
        />
      </SettingsGroup>

      <SettingsGroup>
        <SettingsLabel>Answer Type</SettingsLabel>
        <CheckableSingle
          name="answerType"
          selected={
            properties.interactiveResponse.answerType ||
            ACTIVITIES_ANSWER_MODE_FIXED
          }
          options={[
            {
              label: 'Fixed',
              value: ACTIVITIES_ANSWER_MODE_FIXED,
            },
            {
              label: 'Free',
              value: RESPONSE_FREE,
            },
          ]}
          onSelect={answerType =>
            handleUpdate({
              ...properties,
              interactiveResponse: {
                ...properties.interactiveResponse,
                answerType:
                  answerType === null ||
                  answerType === ACTIVITIES_ANSWER_MODE_FIXED
                    ? ACTIVITIES_ANSWER_MODE_FIXED
                    : answerType,
              },
            })
          }
        />
        {properties.interactiveResponse.answerType !== RESPONSE_FREE && (
          <SmallStyled marginBottom={0}>
            There is only one correct answer.
          </SmallStyled>
        )}
        {properties.interactiveResponse.answerType === RESPONSE_FREE && (
          <SmallStyled marginBottom={0}>
            There is no predetermined answer and students are encouraged to
            provide their own responses based on their creativity and
            understanding.
          </SmallStyled>
        )}
      </SettingsGroup>

      {contentType === LESSON_ACTIVITIES &&
        (properties.interactiveResponse.answerType === null ||
          properties.interactiveResponse.answerType ===
            ACTIVITIES_ANSWER_MODE_FIXED) && (
          <>
            <HrStyled $marginBottom={24} />
            <InteractiveResponseFeedbackSetting
              onSelect={checkAnswer =>
                handleUpdate({
                  ...properties,
                  interactiveResponse: {
                    ...properties.interactiveResponse,
                    checkAnswerAtEnd: checkAnswer === 'end',
                  },
                })
              }
              isCheckAnswerAtEnd={
                properties.interactiveResponse.checkAnswerAtEnd ||
                properties.interactiveResponse.checkAnswerAtEnd === null
              }
            />
          </>
        )}

      <HrStyled $marginBottom={24} />
      {properties.interactiveResponse.answerType === RESPONSE_FREE ? (
        <PStyled>Add a label to each dropzone below.</PStyled>
      ) : (
        <PStyled>
          Add the matching label or rearrange items below in the correct order
          to save your solution.
        </PStyled>
      )}

      <Sortable<InteractiveResponseItem>
        list={items}
        renderItem={(item, index) => {
          const outerItem = items.find(i => i.itemId === item.itemId);
          return (
            <SortableItem key={item.itemId}>
              <SortableHeader />
              {properties.interactiveResponse.answerType !== RESPONSE_FREE && (
                <SortableContent>
                  <FlexSpaceBetween>
                    {isTextVariant(item.variant) ? (
                      <Styled.InteractiveResponseQuestionSequenceItem>
                        <InteractiveDragContainer $scaleTo={54}>
                          <InteractiveDisplayItem $baseColour={item.baseColour}>
                            <InteractiveResponseRichtextView
                              baseColour={item.baseColour}
                              richtext={item.richtext}
                            />
                          </InteractiveDisplayItem>
                        </InteractiveDragContainer>
                      </Styled.InteractiveResponseQuestionSequenceItem>
                    ) : (
                      <ImageThumbnailStatic
                        imageUrl={item.image?.original || ''}
                      />
                    )}
                    <Styled.InteractiveResponseQuestionSequenceText>
                      {index + 1}
                    </Styled.InteractiveResponseQuestionSequenceText>
                  </FlexSpaceBetween>
                </SortableContent>
              )}

              <SortableContent>
                <SettingsLabel>Drop Zone Label</SettingsLabel>
                <AutoResizingTextArea
                  data-selector={`drop-zone-label-${index}`}
                  id={`field-${index}`}
                  name={`field-${index}`}
                  maxLength={60}
                  showCounter
                  rows={1}
                  placeholder="Type here..."
                  initialValue={outerItem?.answerLabel || ''}
                  onChange={event =>
                    handleUpdate({
                      ...properties,
                      interactiveResponse: {
                        ...properties.interactiveResponse,
                        items: items.map(q => {
                          return item.itemId === q.itemId
                            ? {
                                ...q,
                                answerLabel: event.target.value,
                              }
                            : q;
                        }),
                      },
                    })
                  }
                />
              </SortableContent>
            </SortableItem>
          );
        }}
        onSortChange={({ sourceIndex, destinationIndex, direction }) => {
          if (sourceIndex !== destinationIndex) {
            const newSort = getReorderSort<InteractiveResponseItem>(
              items,
              destinationIndex,
              direction
            );
            handleUpdate({
              ...properties,
              interactiveResponse: {
                ...properties.interactiveResponse,
                items: items.map((q, index) => {
                  if (sourceIndex === index) {
                    return {
                      ...q,
                      sort: newSort,
                    };
                  }
                  return q;
                }),
              },
            });
          }
        }}
      />
    </FormProvider>
  );
};

export default InteractiveResponseSolution;
